import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Chip, Collapse, Alert } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { QuestionWithResponsesForAudit } from '../types';
import { ResponseInclusionControls } from './ResponseInclusionControls';
import { QuestionTypeChip } from '../../question/components/QuestionTypeChip';

interface AuditQuestionCardProps {
  question: QuestionWithResponsesForAudit;
  questionIndex: number;
  totalQuestions: number;
  onInclusionChange: (params: {
    questionId: string;
    responseId: string;
    responseIncluded: boolean;
    commentIncluded: boolean;
    hasComment: boolean;
  }) => void;
  isExpanded?: boolean;
  onExpandToggle?: (questionId: string) => void;
  disabled?: boolean;
}

export const AuditQuestionCard: React.FC<AuditQuestionCardProps> = ({
  question,
  questionIndex: _questionIndex,
  totalQuestions: _totalQuestions,
  onInclusionChange,
  isExpanded: controlledExpanded,
  onExpandToggle,
  disabled = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Use controlled expansion if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleExpandClick = () => {
    if (onExpandToggle) {
      onExpandToggle(question.questionId);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const handleInclusionChange = (params: {
    responseId: string;
    responseIncluded: boolean;
    commentIncluded: boolean;
  }) => {
    const response = question.responses?.find((r) => r.id === params.responseId);
    onInclusionChange({
      questionId: question.questionId,
      ...params,
      hasComment: !!response?.comment,
    });
  };

  const getInclusionSummary = () => {
    const includedCount = question.includedCount;
    const totalCount = question.totalResponses;

    // Determine color based on inclusion ratio
    let color: 'success' | 'error' | 'warning' = 'warning';
    if (includedCount === totalCount) {
      color = 'success';
    } else if (includedCount === 0) {
      color = 'error';
    }

    return { text: `${includedCount}/${totalCount} included`, color };
  };

  const inclusionSummary = getInclusionSummary();

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        mb: 2,
        backgroundColor:
          theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
      })}
    >
      <CardContent>
        {/* Question Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
              {question.questionText}
            </Typography>
            {question.questionDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {question.questionDescription}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <QuestionTypeChip type={question.questionType} size="small" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Chip
                  label={inclusionSummary.text}
                  color={inclusionSummary.color}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Button
                onClick={handleExpandClick}
                startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Responses (collapsed/expanded) */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {question.responses.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No responses found for this question.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {question.responses.map((response) => (
                <ResponseInclusionControls
                  key={response.id}
                  response={response}
                  question={question}
                  onInclusionChange={handleInclusionChange}
                  disabled={disabled}
                />
              ))}
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};
